/**
 * Routify Originals — formato editorial próprio.
 *
 * REGRA VINCULATIVA: o payload de uma Original NÃO é uma cópia do
 * `RouteSnapshot`/`ItinerarySnapshot`. Só guarda o que é permitido manter em
 * permanência: `place_id`, coordenadas, sequência, duração sugerida, textos e
 * dicas escritos pela Routify, capas próprias/licenciadas e alternativas
 * (também por `place_id`). Nunca fotos, avaliações, horários ou detalhes
 * brutos vindos do fornecedor de mapas.
 *
 * Módulo puro: sem IO, sem relógio, sem `Math.random`.
 */

import { normalizeTransportMode, type OriginalTransportMode } from "./originals-directions";

export type { OriginalTransportMode };

export const ORIGINAL_SCHEMA_VERSION = 1;

export type OriginalAlternative = {
  placeId: string;
  name: string;
  /** Nota editorial curta da Routify (opcional). */
  note?: string;
  /** Distância aproximada à paragem original, em metros. */
  distanceMeters?: number;
};

export type OriginalStop = {
  /** Identidade estável dentro da rota. */
  id: string;
  /** Dia (1..N). */
  day: number;
  /** Ordem dentro do dia. */
  order: number;
  /** Nome do local (editorial). */
  name: string;
  placeId?: string;
  lat?: number;
  lng?: number;
  /** Duração sugerida da visita, em minutos. */
  suggestedMinutes?: number;
  /** Hora indicativa sugerida ("10:30"), nunca um horário do fornecedor. */
  suggestedTime?: string;
  /** Texto Routify. */
  description?: string;
  /** Dica prática Routify. */
  tip?: string;
  /** Tipo editorial: visita, refeição, deslocação, etc. */
  kind?: "visit" | "meal" | "transfer" | "note";
  alternatives?: OriginalAlternative[];
};

export type OriginalDay = {
  day: number;
  title?: string;
  summary?: string;
  /** Meio de transporte deste dia (omisso = usa o da rota). */
  transportMode?: OriginalTransportMode;
  stops: OriginalStop[];
};

/** Zona editorial (refeição ou dormida). Texto próprio, nunca do fornecedor. */
export type OriginalBase = {
  label: string;
  note?: string;
  /** Dia a que pertence (omisso = vale para a rota toda). */
  day?: number;
  lat?: number;
  lng?: number;
};

/** Link prático curado pela equipa (bilhetes, estacionamento, passes). */
export type OriginalPracticalLink = {
  label: string;
  url: string;
  note?: string;
};

/** Imagem própria ou licenciada, alojada no nosso Storage. */
export type OriginalImage = {
  url: string;
  alt: string;
  caption?: string;
  order: number;
};

export const MAX_ORIGINAL_GALLERY_IMAGES = 6;
export const ORIGINAL_COVER_MAX_BYTES = 350 * 1024;
export const ORIGINAL_GALLERY_MAX_BYTES = 300 * 1024;
export const ORIGINAL_IMAGE_MAX_EDGE = 1600;
export const ORIGINAL_IMAGE_BUCKET = "rotas-pagas";

/** Bloco editorial da página de venda (visível antes da compra). */
export type OriginalSalesInfo = {
  /** Para quem é esta rota. */
  audience?: string;
  /** Para quem NÃO é. */
  notFor?: string;
  /** Ponto de partida (bairro/estação), sem dados do fornecedor. */
  startPoint?: string;
  /** Ritmo do dia (ex.: "3 paragens por manhã, tardes livres"). */
  pace?: string;
  /** Esforço a pé (ex.: "6–8 km por dia, subidas curtas"). */
  walkingEffort?: string;
  /** O que a compra inclui. */
  included?: string[];
  /** O que fica por conta do viajante. */
  notIncluded?: string[];
  /** Quem preparou a rota. */
  preparedBy?: string;
  /** Data da última revisão (AAAA-MM-DD). */
  reviewedOn?: string;
};

export type OriginalRoutePayload = {
  schemaVersion: number;
  /** Introdução editorial. */
  intro?: string;
  /** Informação da página de venda (pública, sem conteúdo pago). */
  sales?: OriginalSalesInfo;
  /** Meio de transporte usado ao abrir a rota no Google Maps. */
  transportMode?: OriginalTransportMode;
  /** Conselhos gerais (o que levar, melhor época, transporte). */
  tips?: string[];
  /** Zonas sugeridas de almoço/jantar. */
  mealBases?: OriginalBase[];
  /** Zonas sugeridas para dormir. */
  sleepBases?: OriginalBase[];
  /** Links oficiais úteis (comboios, estacionamento, passes). */
  practicalLinks?: OriginalPracticalLink[];
  /** Galeria de imagens próprias/licenciadas (até 6). */
  gallery?: OriginalImage[];
  days: OriginalDay[];
};

/** Campos que NUNCA podem entrar num payload de Original. */
const FORBIDDEN_KEYS = new Set([
  "photo",
  "photos",
  "photoName",
  "photo_name",
  "rating",
  "userRatingCount",
  "reviews",
  "openingHours",
  "opening_periods",
  "regularOpeningHours",
  "editorialSummary",
  "formattedAddress",
  "phone",
  "internationalPhoneNumber",
  "website",
]);

export function containsForbiddenProviderData(value: unknown, depth = 0): boolean {
  if (depth > 8 || value == null || typeof value !== "object") return false;
  if (Array.isArray(value)) return value.some((v) => containsForbiddenProviderData(v, depth + 1));
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_KEYS.has(key)) return true;
    if (containsForbiddenProviderData(child, depth + 1)) return true;
  }
  return false;
}

function cleanText(value: unknown, max = 2000): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (!text) return undefined;
  return text.slice(0, max);
}

function cleanNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function cleanAlternative(value: unknown): OriginalAlternative | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  const placeId = cleanText(raw.placeId ?? raw.place_id, 200);
  const name = cleanText(raw.name, 200);
  if (!placeId || !name) return null;
  const note = cleanText(raw.note, 400);
  const distanceMeters = cleanNumber(raw.distanceMeters ?? raw.distance_meters);
  return {
    placeId,
    name,
    ...(note ? { note } : {}),
    ...(distanceMeters != null ? { distanceMeters: Math.round(distanceMeters) } : {}),
  };
}

function cleanStop(value: unknown, fallbackId: string, day: number, order: number): OriginalStop | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  const name = cleanText(raw.name, 200);
  if (!name) return null;
  const kindRaw = cleanText(raw.kind, 20);
  const kind =
    kindRaw === "meal" || kindRaw === "transfer" || kindRaw === "note" ? kindRaw : "visit";
  const alternatives = Array.isArray(raw.alternatives)
    ? raw.alternatives.map(cleanAlternative).filter((a): a is OriginalAlternative => a != null).slice(0, 3)
    : [];
  const placeId = cleanText(raw.placeId ?? raw.place_id, 200);
  const lat = cleanNumber(raw.lat);
  const lng = cleanNumber(raw.lng);
  const suggestedMinutes = cleanNumber(raw.suggestedMinutes ?? raw.suggested_minutes);
  const suggestedTime = cleanText(raw.suggestedTime ?? raw.suggested_time, 5);
  const description = cleanText(raw.description);
  const tip = cleanText(raw.tip, 600);
  return {
    id: cleanText(raw.id, 80) ?? fallbackId,
    day,
    order,
    name,
    kind,
    ...(placeId ? { placeId } : {}),
    ...(lat != null ? { lat } : {}),
    ...(lng != null ? { lng } : {}),
    ...(suggestedMinutes != null ? { suggestedMinutes: Math.round(suggestedMinutes) } : {}),
    ...(suggestedTime ? { suggestedTime } : {}),
    ...(description ? { description } : {}),
    ...(tip ? { tip } : {}),
    ...(alternatives.length ? { alternatives } : {}),
  };
}

function cleanBase(value: unknown): OriginalBase | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  const label = cleanText(raw.label ?? raw.name, 160);
  if (!label) return null;
  const note = cleanText(raw.note, 400);
  const day = cleanNumber(raw.day);
  const lat = cleanNumber(raw.lat);
  const lng = cleanNumber(raw.lng);
  return {
    label,
    ...(note ? { note } : {}),
    ...(day != null ? { day: Math.round(day) } : {}),
    ...(lat != null ? { lat } : {}),
    ...(lng != null ? { lng } : {}),
  };
}

/** Só aceita URLs https; links práticos são escritos à mão pela equipa. */
export function isSafeHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/** Imagens: só do nosso bucket público e só JPG/WebP. */
export function isAllowedOriginalImageUrl(value: unknown): value is string {
  if (!isSafeHttpsUrl(value)) return false;
  const url = new URL(value);
  if (!url.pathname.includes(`/storage/v1/object/public/${ORIGINAL_IMAGE_BUCKET}/`)) return false;
  return /\.(jpe?g|webp)$/i.test(url.pathname);
}

function cleanPracticalLink(value: unknown): OriginalPracticalLink | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  const label = cleanText(raw.label, 120);
  const url = cleanText(raw.url, 600);
  if (!label || !isSafeHttpsUrl(url)) return null;
  const note = cleanText(raw.note, 240);
  return { label, url, ...(note ? { note } : {}) };
}

function cleanImage(value: unknown, fallbackOrder: number): OriginalImage | null {
  const raw = (value ?? {}) as Record<string, unknown>;
  const url = cleanText(raw.url, 600);
  const alt = cleanText(raw.alt, 200);
  if (!alt || !isAllowedOriginalImageUrl(url)) return null;
  const caption = cleanText(raw.caption, 240);
  const order = cleanNumber(raw.order);
  return {
    url: url!,
    alt,
    ...(caption ? { caption } : {}),
    order: order != null ? Math.round(order) : fallbackOrder,
  };
}

/**
 * Normaliza QUALQUER entrada (formulário do painel ou conversão de uma
 * geração) para o formato editorial permitido. Tudo o que não está previsto
 * é descartado — é isto que impede uma cópia cega de dados do fornecedor.
 */
export function normalizeOriginalPayload(value: unknown): OriginalRoutePayload {
  const raw = (value ?? {}) as Record<string, unknown>;
  const rawDays = Array.isArray(raw.days) ? raw.days : [];
  const days: OriginalDay[] = [];
  rawDays.forEach((dayValue, dayIndex) => {
    const rawDay = (dayValue ?? {}) as Record<string, unknown>;
    const dayNumber = cleanNumber(rawDay.day) ?? dayIndex + 1;
    const rawStops = Array.isArray(rawDay.stops) ? rawDay.stops : [];
    const stops: OriginalStop[] = [];
    rawStops.forEach((stopValue, stopIndex) => {
      const stop = cleanStop(stopValue, `d${dayNumber}-s${stopIndex + 1}`, dayNumber, stopIndex);
      if (stop) stops.push(stop);
    });
    days.push({
      day: dayNumber,
      ...(cleanText(rawDay.title, 200) ? { title: cleanText(rawDay.title, 200)! } : {}),
      ...(cleanText(rawDay.summary) ? { summary: cleanText(rawDay.summary)! } : {}),
      ...(rawDay.transportMode
        ? { transportMode: normalizeTransportMode(rawDay.transportMode) }
        : {}),
      stops,
    });
  });
  const tips = Array.isArray(raw.tips)
    ? raw.tips.map((t) => cleanText(t, 400)).filter((t): t is string => !!t).slice(0, 12)
    : [];
  const mealBases = Array.isArray(raw.mealBases)
    ? raw.mealBases.map(cleanBase).filter((b): b is OriginalBase => b != null).slice(0, 24)
    : [];
  const sleepBases = Array.isArray(raw.sleepBases)
    ? raw.sleepBases.map(cleanBase).filter((b): b is OriginalBase => b != null).slice(0, 24)
    : [];
  const practicalLinks = Array.isArray(raw.practicalLinks)
    ? raw.practicalLinks
        .map(cleanPracticalLink)
        .filter((l): l is OriginalPracticalLink => l != null)
        .slice(0, 12)
    : [];
  const gallery = Array.isArray(raw.gallery)
    ? raw.gallery
        .map((image, index) => cleanImage(image, index))
        .filter((i): i is OriginalImage => i != null)
        .sort((a, b) => a.order - b.order)
        .slice(0, MAX_ORIGINAL_GALLERY_IMAGES)
        .map((image, index) => ({ ...image, order: index }))
    : [];
  const sales = cleanSalesInfo(raw.sales);
  return {
    schemaVersion: ORIGINAL_SCHEMA_VERSION,
    ...(cleanText(raw.intro) ? { intro: cleanText(raw.intro)! } : {}),
    ...(sales ? { sales } : {}),
    ...(raw.transportMode ? { transportMode: normalizeTransportMode(raw.transportMode) } : {}),
    ...(tips.length ? { tips } : {}),
    ...(mealBases.length ? { mealBases } : {}),
    ...(sleepBases.length ? { sleepBases } : {}),
    ...(practicalLinks.length ? { practicalLinks } : {}),
    ...(gallery.length ? { gallery } : {}),
    days,
  };
}

function cleanStringList(value: unknown, max: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => cleanText(v, 200))
    .filter((v): v is string => !!v)
    .slice(0, max);
}

function cleanSalesInfo(value: unknown): OriginalSalesInfo | undefined {
  const raw = (value ?? {}) as Record<string, unknown>;
  const reviewedRaw = cleanText(raw.reviewedOn, 10);
  const info: OriginalSalesInfo = {
    ...(cleanText(raw.audience, 400) ? { audience: cleanText(raw.audience, 400)! } : {}),
    ...(cleanText(raw.notFor, 400) ? { notFor: cleanText(raw.notFor, 400)! } : {}),
    ...(cleanText(raw.startPoint, 200) ? { startPoint: cleanText(raw.startPoint, 200)! } : {}),
    ...(cleanText(raw.pace, 200) ? { pace: cleanText(raw.pace, 200)! } : {}),
    ...(cleanText(raw.walkingEffort, 200)
      ? { walkingEffort: cleanText(raw.walkingEffort, 200)! }
      : {}),
    ...(cleanText(raw.preparedBy, 120) ? { preparedBy: cleanText(raw.preparedBy, 120)! } : {}),
    ...(reviewedRaw && /^\d{4}-\d{2}-\d{2}$/.test(reviewedRaw) ? { reviewedOn: reviewedRaw } : {}),
  };
  const included = cleanStringList(raw.included, 12);
  const notIncluded = cleanStringList(raw.notIncluded, 12);
  if (included.length) info.included = included;
  if (notIncluded.length) info.notIncluded = notIncluded;
  return Object.keys(info).length ? info : undefined;
}

/** Campos obrigatórios para colocar uma Original à venda. */
export function missingSalesFields(
  payload: OriginalRoutePayload,
  card: { heroImageUrl?: string | null; priceCents?: number | null },
): string[] {
  const sales = payload.sales ?? {};
  const missing: string[] = [];
  if (!sales.audience) missing.push("Para quem é esta rota");
  if (!sales.startPoint) missing.push("Ponto de partida");
  if (!sales.pace) missing.push("Ritmo do dia");
  if (!sales.walkingEffort) missing.push("Esforço a pé");
  if (!sales.included?.length) missing.push("O que recebes");
  if (!sales.notIncluded?.length) missing.push("O que fica por tua conta");
  if (!sales.preparedBy) missing.push("Quem preparou a rota");
  if (!sales.reviewedOn) missing.push("Data da última revisão");
  if (!card.heroImageUrl) missing.push("Imagem de capa");
  if (!(card.priceCents && card.priceCents >= 50)) missing.push("Preço");
  if (!payload.tips?.length) missing.push("Pelo menos uma dica");
  if (!payload.days.some((d) => d.stops.length > 0)) missing.push("Pelo menos uma paragem");
  return missing;
}

/** Link Google Maps de uma paragem — construído localmente, sem chamadas pagas. */
export function stopMapsUrl(stop: {
  name: string;
  placeId?: string;
  lat?: number;
  lng?: number;
}): string {
  const query =
    stop.lat != null && stop.lng != null ? `${stop.lat},${stop.lng}` : stop.name;
  const params = new URLSearchParams({ api: "1", query });
  if (stop.placeId) params.set("query_place_id", stop.placeId);
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

/** Link de pesquisa por texto (zonas de refeição/dormida). */
export function areaMapsUrl(label: string, lat?: number, lng?: number): string {
  const query = lat != null && lng != null ? `${lat},${lng}` : label;
  return `https://www.google.com/maps/search/?${new URLSearchParams({ api: "1", query }).toString()}`;
}

/** Percurso do dia inteiro no Maps (origem, destino e até 8 pontos pelo meio). */
export function dayDirectionsUrl(
  stops: Array<{ name: string; placeId?: string; lat?: number; lng?: number }>,
): string | null {
  const points = stops.filter((s) => s.lat != null && s.lng != null || s.name);
  if (points.length < 2) return null;
  const asText = (s: { name: string; lat?: number; lng?: number }) =>
    s.lat != null && s.lng != null ? `${s.lat},${s.lng}` : s.name;
  const origin = points[0]!;
  const destination = points[points.length - 1]!;
  const middle = points.slice(1, -1).slice(0, 8);
  const params = new URLSearchParams({
    api: "1",
    origin: asText(origin),
    destination: asText(destination),
    travelmode: "walking",
  });
  if (origin.placeId) params.set("origin_place_id", origin.placeId);
  if (destination.placeId) params.set("destination_place_id", destination.placeId);
  if (middle.length) params.set("waypoints", middle.map(asText).join("|"));
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}


/**
 * Converte uma geração normal num payload editorial, extraindo APENAS os
 * campos permitidos. Nunca copia o snapshot inteiro.
 */
export function originalPayloadFromGeneration(generation: unknown): OriginalRoutePayload {
  const raw = (generation ?? {}) as Record<string, unknown>;
  const entries = Array.isArray(raw.entries) ? raw.entries : [];
  const byDay = new Map<number, OriginalStop[]>();
  entries.forEach((entryValue, index) => {
    const entry = (entryValue ?? {}) as Record<string, unknown>;
    const day = cleanNumber(entry.day) ?? 1;
    const name = cleanText(entry.name, 200) ?? cleanText(entry.label, 200);
    if (!name) return;
    const list = byDay.get(day) ?? [];
    const stop = cleanStop(
      {
        id: entry.logicalId ?? entry.scheduleEntryId,
        name,
        placeId: entry.placeId,
        lat: entry.lat,
        lng: entry.lng,
        description: entry.blurb,
        kind: entry.kind === "break" ? "meal" : "visit",
      },
      `d${day}-s${index + 1}`,
      day,
      list.length,
    );
    if (stop) {
      list.push(stop);
      byDay.set(day, list);
    }
  });
  const days: OriginalDay[] = [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([day, stops]) => ({ day, stops }));
  return normalizeOriginalPayload({ days });
}

/** Hash estável do conteúdo publicado (djb2 sobre JSON canónico). */
export function contentHash(payload: OriginalRoutePayload): string {
  const json = canonicalJson(payload);
  let hash = 5381;
  for (let i = 0; i < json.length; i += 1) {
    hash = ((hash << 5) + hash + json.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

function canonicalJson(value: unknown): string {
  if (value == null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${canonicalJson((value as Record<string, unknown>)[k])}`)
    .join(",")}}`;
}

export type OriginalPreview = OriginalRoutePayload & { locked: true; hiddenStops: number };

/**
 * Pré-visualização gratuita e honesta: primeiro dia com as duas primeiras
 * paragens; os restantes dias mostram apenas o título e quantas paragens têm.
 */
export function previewOriginalPayload(payload: OriginalRoutePayload): OriginalPreview {
  let hidden = 0;
  const days = payload.days.map((day, index) => {
    const keep = index === 0 ? 2 : 0;
    hidden += Math.max(0, day.stops.length - keep);
    return {
      ...day,
      stops: day.stops.slice(0, keep).map((s) => ({ ...s, alternatives: [] })),
    };
  });
  // Zonas de refeição/dormida e links úteis fazem parte do que se compra.
  const { mealBases: _m, sleepBases: _s, practicalLinks: _p, ...rest } = payload;
  return { ...rest, days, locked: true, hiddenStops: hidden };
}

export function countOriginalStops(payload: OriginalRoutePayload): number {
  return payload.days.reduce((total, day) => total + day.stops.length, 0);
}

export function formatOriginalPrice(cents: number, currency: string): string {
  const value = (Math.max(0, Math.round(cents)) / 100).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency.toLowerCase() === "eur" ? `€${value}` : `${value} ${currency.toUpperCase()}`;
}
